import type {
  EngagementAchievementMetric,
  EngagementAchievementMoneyMetric,
  EngagementAchievementsData,
  EngagementCampaignPerformanceRow,
  EngagementChange,
  EngagementTimeSeriesRow,
} from "src/services/EngagementOverviewAPI";
import {
  ENGAGEMENT_CAMPAIGN_BAR_COLORS,
  type CampaignPerformanceDatum,
  type EngagementBusinessImpactMetric,
  type TimeSeriesDatum,
  type WebsiteUsersBreakdown,
} from "../Types/EngagementOverviewTypes";

export const toChangePercent = (change: EngagementChange | null | undefined): number | null =>
  typeof change === "number" && Number.isFinite(change) ? change : null;

export const toNumber = (value: number | null | undefined): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

export const formatEngagementCount = (value: number | null | undefined): string =>
  toNumber(value).toLocaleString();

export const formatEngagementRate = (value: number | null | undefined, digits = 1): string =>
  `${toNumber(value).toFixed(digits)}%`;

export const clampToChartScale = (value: number): number => Math.max(0, Math.min(100, value));

const FORMATTED_KEYS = [
  "formattedValue",
  "valueFormattedValue",
  "formatted_value",
] as const;

export const readFormattedText = (input: unknown): string | undefined => {
  if (typeof input === "string" && input.trim()) {
    return input.trim();
  }
  if (input && typeof input === "object") {
    const record = input as Record<string, unknown>;
    for (const key of FORMATTED_KEYS) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }
  return undefined;
};

export const readNumericValue = (input: unknown): number | null => {
  if (typeof input === "number" && Number.isFinite(input)) {
    return input;
  }
  if (input && typeof input === "object" && "value" in input) {
    const nested = (input as { value?: unknown }).value;
    if (typeof nested === "number" && Number.isFinite(nested)) {
      return nested;
    }
  }
  return null;
};

export const formattedFromField = (
  record: Record<string, unknown> | null | undefined,
  field: string,
): string | undefined => {
  if (!record) {
    return undefined;
  }
  return readFormattedText(record[`${field}FormattedValue`]) ?? readFormattedText(record[field]);
};

export const displayFromField = (
  record: Record<string, unknown> | null | undefined,
  field: string,
  fallback: (_value: number) => string,
): string => {
  const formatted = formattedFromField(record, field);
  if (formatted) {
    return formatted;
  }
  return fallback(readNumericValue(record?.[field]) ?? 0);
};

export const changeFromField = (
  record: Record<string, unknown> | null | undefined,
  field: string,
): { percent: number | null; formatted?: string } => {
  if (!record) {
    return { percent: null };
  }
  const metric = record[field];
  return {
    percent: toChangePercent(
      (readNumericValue(metric) ??
        (typeof metric === "number" || metric === "-" ? metric : null)) as EngagementChange | null,
    ),
    formatted:
      readFormattedText(record[`${field}FormattedValue`]) ?? readFormattedText(metric),
  };
};

export const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const achievementValue = (
  metric: EngagementAchievementMetric | EngagementAchievementMoneyMetric | null | undefined,
): string => {
  const formatted = readFormattedText(metric);
  if (formatted) {
    return formatted;
  }
  return formatEngagementCount(metric?.value);
};

const achievementChange = (
  metric: EngagementAchievementMetric | EngagementAchievementMoneyMetric | null | undefined,
): { percent: number | null; formatted?: string } => ({
  percent: toChangePercent(readNumericValue(metric?.percent_change) ?? metric?.percent_change ?? null),
  formatted:
    readFormattedText(metric?.percent_changeFormattedValue) ??
    readFormattedText(metric?.percent_change),
});

export const mapAchievements = (
  data: EngagementAchievementsData | null,
): EngagementBusinessImpactMetric[] => {
  if (!data) {
    return [];
  }

  const item = (
    id: string,
    title: string,
    metric: EngagementAchievementMetric | EngagementAchievementMoneyMetric | null | undefined,
  ): EngagementBusinessImpactMetric => {
    const change = achievementChange(metric);
    return {
      id,
      title,
      value: achievementValue(metric),
      changePercent: change.percent,
      changePercentFormatted: change.formatted,
    };
  };

  return [
    item("orders", "Orders", data.total_orders),
    item("revenue", "Revenue", data.total_sales),
    item("totalUsers", "Total Users", data.total_users),
    item("totalCustomers", "Total Customers", data.total_customers),
  ];
};

export const mapWebsiteUsers = (
  data: EngagementAchievementsData | null,
): WebsiteUsersBreakdown | null => {
  const total = toNumber(data?.total_users?.value);
  if (!data || total <= 0) {
    return null;
  }

  const totalUsersFormatted = achievementValue(data.total_users);

  return {
    totalUsers: total,
    totalUsersFormatted,
    slices: [
      {
        name: "Total Users",
        value: total,
        color: ENGAGEMENT_CAMPAIGN_BAR_COLORS.openRate,
        legendPercent: 100,
        valueFormatted: totalUsersFormatted,
      },
    ],
  };
};

export const mapCampaignPerformance = (
  rows: EngagementCampaignPerformanceRow[] | null | undefined,
): CampaignPerformanceDatum[] => {
  const safeRows = rows ?? [];
  const maxSent = safeRows.reduce((max, row) => Math.max(max, toNumber(row?.sent)), 0);

  const nameTotals = new Map<string, number>();
  safeRows.forEach(row => {
    const name = row?.campaign_name?.trim() || "Untitled campaign";
    nameTotals.set(name, (nameTotals.get(name) ?? 0) + 1);
  });

  const nameSeen = new Map<string, number>();

  return safeRows.map((row, index) => {
    const name = row?.campaign_name?.trim() || "Untitled campaign";
    const occurrence = (nameSeen.get(name) ?? 0) + 1;
    nameSeen.set(name, occurrence);
    const label = (nameTotals.get(name) ?? 0) > 1 ? `${name} (${occurrence})` : name;

    const sent = toNumber(row?.sent);
    const openRate = toNumber(row?.open_rate);
    const clickRate = toNumber(row?.click_rate);
    const clickThroughRate = toNumber(row?.click_through_rate);
    const record = asRecord(row);

    return {
      id: row?.campaign_id || `${label}-${index}`,
      label,
      medium: row?.medium ?? null,
      openRate,
      clickRate,
      totalSent: sent,
      clickThroughRate,
      openRateValue: clampToChartScale(openRate),
      clickRateValue: clampToChartScale(clickRate),
      totalSentValue: maxSent > 0 ? clampToChartScale((sent / maxSent) * 100) : 0,
      clickThroughRateValue: clampToChartScale(clickThroughRate),
      openRateFormatted: formattedFromField(record, "open_rate"),
      clickRateFormatted: formattedFromField(record, "click_rate"),
      totalSentFormatted: formattedFromField(record, "sent"),
      clickThroughRateFormatted: formattedFromField(record, "click_through_rate"),
    } satisfies CampaignPerformanceDatum;
  });
};

interface TimeSeriesAccumulator {
  date: string;
  sent: number;
  opened: number;
  clicks: number;
  openRateWeighted: number;
  clickRateWeighted: number;
  clickThroughRateWeighted: number;
  bounceRateWeighted: number;
  openRateSum: number;
  clickRateSum: number;
  clickThroughRateSum: number;
  bounceRateSum: number;
  rows: number;
}

const emptyAccumulator = (date: string): TimeSeriesAccumulator => ({
  date,
  sent: 0,
  opened: 0,
  clicks: 0,
  openRateWeighted: 0,
  clickRateWeighted: 0,
  clickThroughRateWeighted: 0,
  bounceRateWeighted: 0,
  openRateSum: 0,
  clickRateSum: 0,
  clickThroughRateSum: 0,
  bounceRateSum: 0,
  rows: 0,
});

const accumulate = (
  bucket: TimeSeriesAccumulator,
  sent: number,
  opened: number,
  clicks: number,
  openRate: number,
  clickRate: number,
  clickThroughRate: number,
  bounceRate: number,
): void => {
  bucket.sent += sent;
  bucket.opened += opened;
  bucket.clicks += clicks;
  bucket.openRateWeighted += openRate * sent;
  bucket.clickRateWeighted += clickRate * sent;
  bucket.clickThroughRateWeighted += clickThroughRate * sent;
  bucket.bounceRateWeighted += bounceRate * sent;
  bucket.openRateSum += openRate;
  bucket.clickRateSum += clickRate;
  bucket.clickThroughRateSum += clickThroughRate;
  bucket.bounceRateSum += bounceRate;
  bucket.rows += 1;
};

const finalizeBucket = (bucket: TimeSeriesAccumulator): TimeSeriesDatum => {
  const rate = (weighted: number, plainSum: number) => {
    if (bucket.sent > 0) {
      return weighted / bucket.sent;
    }
    return bucket.rows > 0 ? plainSum / bucket.rows : 0;
  };

  return {
    date: bucket.date,
    sent: bucket.sent,
    opened: bucket.opened,
    clicks: bucket.clicks,
    openRate: rate(bucket.openRateWeighted, bucket.openRateSum),
    clickRate: rate(bucket.clickRateWeighted, bucket.clickRateSum),
    clickThroughRate: rate(bucket.clickThroughRateWeighted, bucket.clickThroughRateSum),
    bounceRate: rate(bucket.bounceRateWeighted, bucket.bounceRateSum),
  };
};

export const mapTimeSeries = (
  rows: EngagementTimeSeriesRow[] | null | undefined,
): TimeSeriesDatum[] => {
  const buckets = new Map<string, TimeSeriesAccumulator>();

  (rows ?? []).forEach(row => {
    const date = row?.date;
    if (!date) {
      return;
    }
    const bucket = buckets.get(date) ?? emptyAccumulator(date);
    accumulate(
      bucket,
      toNumber(row?.sent),
      toNumber(row?.opened),
      toNumber(row?.clicks),
      toNumber(row?.open_rate),
      toNumber(row?.click_rate),
      toNumber(row?.click_through_rate),
      toNumber(row?.bounce_rate),
    );
    buckets.set(date, bucket);
  });

  return Array.from(buckets.values())
    .sort((left, right) => left.date.localeCompare(right.date))
    .map(bucket => {
      const matching = (rows ?? []).filter(row => (row?.date || "") === bucket.date);
      const source = matching.length === 1 ? matching[0] : undefined;
      const record = asRecord(source);
      const point = finalizeBucket(bucket);
      return {
        ...point,
        dateFormatted: readFormattedText(source?.dateFormattedValue),
        clicksFormatted: formattedFromField(record, "clicks"),
        openRateFormatted: formattedFromField(record, "open_rate"),
        bounceRateFormatted: formattedFromField(record, "bounce_rate"),
      };
    });
};

export const aggregateTimeSeriesMonthly = (series: TimeSeriesDatum[]): TimeSeriesDatum[] => {
  const buckets = new Map<string, TimeSeriesAccumulator>();

  (series ?? []).forEach(point => {
    const monthKey = `${point.date.slice(0, 7)}-01`;
    const bucket = buckets.get(monthKey) ?? emptyAccumulator(monthKey);
    accumulate(
      bucket,
      point.sent,
      point.opened,
      point.clicks,
      point.openRate,
      point.clickRate,
      point.clickThroughRate,
      point.bounceRate,
    );
    buckets.set(monthKey, bucket);
  });

  return Array.from(buckets.values())
    .sort((left, right) => left.date.localeCompare(right.date))
    .map(finalizeBucket);
};
